import { ITeamResponseSchema } from '@monetize/types/app';
import { MdOutlineHistory } from 'react-icons/md';
import {
  MCustomIconButton,
  MFlex,
  MSettingsPageHeader,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { MDataTableActions } from '../../../../../components/Monetize/DataTable';
import { InlineEditableText } from '../../../../../components/Monetize/InlineEditableText';
import { TEAM_TYPE_DISPLAY } from '../../../../../constants/teams';
import { useHandleDeleteTeam } from '../useHandleDeleteTeam';

interface TeamDetailsHeaderProps {
  team: ITeamResponseSchema;
  isHistoryOpen: boolean;
  isMutating: boolean;
  /** True when the team has more than one active member, so ownership can move. */
  canChangeOwner: boolean;
  onOpenHistory: () => void;
  onRenameTeam: (name: string) => Promise<void>;
  onChangeOwner: () => void;
}

export const TeamDetailsHeader = ({
  team,
  isHistoryOpen,
  isMutating,
  canChangeOwner,
  onOpenHistory,
  onRenameTeam,
  onChangeOwner,
}: TeamDetailsHeaderProps) => {
  const { handleDeleteTeam } = useHandleDeleteTeam();
  const { name: teamName, id: teamId, type: teamType } = team;

  // Owner change lives here (not on the owner's row) so it stays reachable
  // even when the owner row is filtered out of the members table.
  const actions = [
    {
      title: 'Change Owner',
      enabled: true,
      isDisabled: !canChangeOwner || isMutating,
      action: onChangeOwner,
    },
    {
      title: 'Delete',
      color: 'danger',
      enabled: true,
      isDisabled: isMutating,
      action: () => handleDeleteTeam({ teamName, teamId }),
    },
  ];

  return (
    <MSettingsPageHeader
      divider={false}
      hasBackButton
      title={
        <InlineEditableText
          value={teamName}
          isRequired
          isReadOnly={isMutating}
          onSave={onRenameTeam}
          editButtonAriaLabel="Edit team name"
          data-testid="team-name-inline-edit"
        />
      }
      id={teamId}
      extraSubtitleParts={[
        <MText key="team-type">{TEAM_TYPE_DISPLAY[teamType]}</MText>,
      ]}
      recordAuditInfo={team}
    >
      <MFlex alignItems="center" columnGap={2}>
        {!isHistoryOpen && (
          <MTooltip label="View History" placement="bottom-end">
            <MCustomIconButton
              icon={MdOutlineHistory}
              containerSize={8}
              btnSize={6}
              variant="icon"
              aria-label="Team History"
              onClick={onOpenHistory}
              data-testid="team-history-open-btn"
            />
          </MTooltip>
        )}
        <MDataTableActions actions={actions} />
      </MFlex>
    </MSettingsPageHeader>
  );
};
